import { useState } from "react";

import {
  LABEEB_TIERS,
  labeebPaged,
  type LabeebTierRow,
} from "../../api/labeeb";
import {
  LABEEB_LIMIT_KEYS,
  labeebTierHref,
  limitLabel,
  limitValue,
} from "../../api/labeebLimits";
import type { McpCatalog, McpQuotaAll, McpTenantList, McpTiers } from "../../api/mcp";
import {
  capLabel,
  metricPath,
  tenantsOf,
  tierDocsOf,
  tiersOf,
  toolCapsOf,
  type FleetMetricEnvelope,
  type McpQuotaDoc,
} from "../../api/mcp";
import { LabeebNewTier } from "../../components/labeeb/LabeebNewTier";
import { McpNewTier } from "../../components/mcp/McpNewTier";
import { LoadMore } from "../../components/LoadMore";
import { EmptyByDesign, ResourceView, statOf } from "../../components/states";
import { Badge, Card, Notice, Page, Panel, Segmented, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { labeebPage, usePagedList } from "../../hooks/usePagedList";
import { useResource } from "../../hooks/useResource";
import { Bars } from "../../components/charts";
import { ProductSection } from "../../products/PerProduct";

/**
 * Tiers & credits — one section per product, which is what the design draws.
 *
 * A "tier" is not one thing. It is a row in Labeeb's store and a row in MCP's,
 * and they are set independently:
 *
 *   Labeeb   the envelope a merchant buys — messages a day, messages a month,
 *            tokens, Snowflake queries — and the feature set the tier includes.
 *   MCP      each tier's quota document from GET /admin/quota: a default cap
 *            for any tool without its own, an optional daily total, and
 *            per-tool caps.
 *
 * Splitting them by product is not presentation. The design's whole reason for
 * a "Stored as" cell is that the two can disagree, and the note it carries —
 * "MCP has no Enterprise row, Enterprise resolves to Pro" — is exactly such a
 * disagreement, load-bearing and invisible in a merged table.
 */
export function Tiers({ config }: { config: AppConfig }) {
  const quota = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  // STORED vs EFFECTIVE is a real distinction on this API and an easy one to
  // get wrong: a merchant with no entitlements row is stored as nothing and
  // resolved as free. Counting them under free would overstate how many
  // deliberate free-tier decisions exist; counting them nowhere hides that
  // free is what they actually get. The switch shows both rather than picking.
  const [basis, setBasis] = useState<"stored" | "effective">("stored");
  // Real, and the design asks for it: how many merchants sit on each tier.
  // Counted client-side from the list the console already reads — MCP serves
  // no per-tier count of its own.
  const tenants = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  // The set a merchant may be assigned to. Quota documents say what each
  // tier stores; this list says which tiers exist, including one with no
  // document yet (`free` is always here).
  const validTiers = useResource<McpTiers>(config, "/mcp/admin/tiers");
  // Real: GET /labeeb/admin/tiers, the envelope panel below.
  const labeebTiers = usePagedList<LabeebTierRow>(config, labeebPaged(LABEEB_TIERS), labeebPage);

  // The fleet figures, and the tier they narrow both blocks to. Clicking a
  // tier is how an operator gets from "8 merchants on Plus" to those eight
  // rows — the count is the way in, not a number to go hunting after.
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const tenantsCount = useResource<FleetMetricEnvelope<"tenants_count">>(
    config,
    metricPath("tenants_count"),
  );
  const usageToday = useResource<FleetMetricEnvelope<"usage_today">>(
    config,
    metricPath("usage_today"),
  );
  const overrides = useResource<FleetMetricEnvelope<"quota_overrides_count">>(
    config,
    metricPath("quota_overrides_count"),
  );

  return (
    <Page
      title="Tiers & limits"
      subtitle="What a tier entitles a merchant to, in each product that stores one."
    >
      <div className="flex flex-col gap-6">
        <TierMetrics
          tenantsCount={tenantsCount}
          usageToday={usageToday}
          overrides={overrides}
          tierFilter={tierFilter}
          onFilter={setTierFilter}
        />

        {/* ONE BLOCK PER PRODUCT. A tier is two rows in two stores — Labeeb's
            message/token envelope and MCP's per-tool credit caps — and they
            are set independently. They used to be three peer panels plus a
            separate "Tool caps" page reading the SAME /mcp/admin/quota this
            one does, with a Notice telling the reader to go there to change
            anything. */}
        <ProductSection id="labeeb" note="The daily and monthly limits each tier grants">
        <Card
          title="Envelope shape"
          note="What each tier grants, and whether it is a stored row or a code fallback"
        >
          <ResourceView state={labeebTiers.state} what="tier envelopes" onRetry={labeebTiers.reload}>
            {(data) => {
              // Narrowed by the tier filter above. Labeeb's ids are the same
              // strings MCP's distribution counts, so one filter drives both.
              const rows = data.rows.filter(
                (r) => !tierFilter || r.tier_id === tierFilter,
              );
              // FIXED COLUMNS now. This used to union the keys present across
              // every row, because the envelope was an open map and a renamed
              // limit should shrink the table rather than leave a blank column.
              // `TierOut` has exactly two named integers, so there is nothing
              // left to discover.
              const cols = [...LABEEB_LIMIT_KEYS];

              return rows.length === 0 ? (
                <Notice title="No tiers returned">
                  The response carried an empty or missing tier list.
                </Notice>
              ) : (
                <Panel className="p-4">
                  <Table
                    head={
                      <tr>
                        <Th>Tier</Th>
                        {cols.map((c) => (
                          <Th key={c} right>
                            {limitLabel(c)}
                          </Th>
                        ))}
                        <Th right>Open</Th>
                      </tr>
                    }
                  >
                    {rows.map((row) => (
                      <tr key={row.tier_id}>
                        <Td>
                          {/* The id, not a display name: `TierOut` no longer
                              carries one, and fetching the catalog solely to
                              prettify a label would add a request that can fail
                              and leave the column blank. The id is what a write
                              submits, so it is also the safer thing to show. */}
                          <span className="ref">{row.tier_id}</span>
                          {/* `source: "code_default"` is gone — a tier now
                              either exists or the route 404s, so there is no
                              "saving creates it" case left to warn about.
                              `is_default` is a different fact and worth its own
                              badge: it is the tier a business lands on. */}
                          {row.is_default && <Badge>default</Badge>}
                        </Td>
                        {cols.map((c) => (
                          <Td key={c} right>
                            <span className="tabular-nums">{limitValue(row, c)}</span>
                          </Td>
                        ))}
                        <Td right>
                          {/* A link, not an expander. The editor used to open
                              inside this cell, which is why its fields, its two
                              warnings and its result all had to fit in a column
                              — MCP's equivalent got a page. This is that page. */}
                          <Button outline href={labeebTierHref(row.tier_id)}>
                            Open
                          </Button>
                        </Td>
                      </tr>
                    ))}
                  </Table>

                  {/* ONE TIER IS THE NORMAL STATE, not a truncated read.
                      Labeeb 2.0 stores tiers in its own single table
                      (pk=QUOTA_TIERS, sk=TIER#<id>) and the deploy bootstraps
                      exactly one, `standard`. The v1 stack's QuotaTiers table
                      has more rows and Labeeb 2.0 never reads it.
                      Said here because "only standard" looks like a bug — it
                      cost a round of investigation to establish that it is not,
                      and the next person should not repeat that. */}
                  {rows.length === 1 && !data.next && (
                    <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                      One tier is expected on a fresh environment: the deploy bootstraps{" "}
                      <span className="ref">standard</span> and nothing else. More tiers appear
                      once somebody creates them — there is no seed script, by design, so every
                      tier gets a version and an audit row.
                    </p>
                  )}
                  <LoadMore
                    shown={data.rows.length}
                    noun="tiers"
                    hasMore={data.next !== null}
                    loading={labeebTiers.loadingMore}
                    error={labeebTiers.moreError}
                    onMore={labeebTiers.loadMore}
                  />
                </Panel>
              );
            }}
          </ResourceView>
        </Card>

        <LabeebNewTier config={config} onCreated={labeebTiers.reload} />

        {/* The right-hand half of the design's Envelope shape, for the product
            that CAN answer it. MCP knows which tier each merchant is on, so
            this is real — and the switch is the one the design's metric
            selector idiom is worth spending on. */}
        </ProductSection>

        <ProductSection
          id="mcp"
          note="Per-tool daily credit caps, and how tenants are spread across tiers"
        >
        <div className="flex flex-col gap-6">
        <McpNewTier
          config={config}
          onCreated={() => {
            quota.reload();
            validTiers.reload();
          }}
        />
        <Card
          title="Tenants on each tier"
          note="How the fleet is distributed, by what is stored and by what the server resolves"
        >
          <ResourceView state={tenants.state} what="the tier distribution" onRetry={tenants.reload}>
            {(data) => {
              const all = tenantsOf(data);
              const stored = new Map<string, number>();
              for (const m of all) {
                const key = basis === "stored" ? (m.tier ?? "not set") : (m.tier ?? "free");
                stored.set(key, (stored.get(key) ?? 0) + 1);
              }
              const rows = [...stored.entries()]
                .sort(([a], [b]) => (a === "not set" ? 1 : b === "not set" ? -1 : a.localeCompare(b)))
                .map(([tier, n]) => ({
                  label: tier,
                  value: n,
                  note: `of ${all.length}`,
                  // "not set" is not a tier — it is merchants nobody has
                  // touched, and it deserves its own tone rather than sitting
                  // in the same colour as a decision someone made.
                  tone: tier === "not set" ? ("mute" as const) : ("brand" as const),
                }));

              return (
                <>
                  <div className="mb-4 flex justify-end">
                    <Segmented
                      label="Count tenants by"
                      value={basis}
                      onChange={setBasis}
                      options={[
                        { value: "stored", label: "Stored" },
                        { value: "effective", label: "Effective" },
                      ]}
                    />
                  </div>
                  <Bars rows={rows} max={all.length} />

                  {/* The same counts, as filters. The chart SHOWS the spread;
                      these act on it — clicking one narrows both product
                      blocks to that tier, and clicking it again clears. */}
                  <div className="mt-4">
                    <StatRow>
                      {rows.map((r) => (
                        <Stat
                          key={r.label}
                          value={r.value}
                          label={r.label}
                          onClick={() =>
                            setTierFilter(tierFilter === r.label ? null : r.label)
                          }
                          active={tierFilter === r.label}
                        />
                      ))}
                    </StatRow>
                  </div>
                  <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                    {basis === "stored" ? (
                      <>
                        What is written down. <span className="ref">not set</span> is a merchant
                        with no entitlements row — nothing has been decided for them, and a read
                        never creates one.
                      </>
                    ) : (
                      <>
                        What the server applies. Every merchant with no row resolves as{" "}
                        <span className="ref">free</span>, so free grows by exactly the number
                        that were <span className="ref">not set</span>.
                      </>
                    )}
                  </p>
                </>
              );
            }}
          </ResourceView>
        </Card>

        {/* The per-tier cap rows. They read the same /mcp/admin/quota as the
            summary above and write nothing, so they sit on the summary. The
            editor stays on /policy/caps/{tier}: that page writes. */}
        <ResourceView state={validTiers.state} what="the valid tiers" onRetry={validTiers.reload}>
          {(data) => (
            <CapRows
              config={config}
              quota={quota}
              tierFilter={tierFilter}
              tiers={tiersOf(data)}
            />
          )}
        </ResourceView>
        </div>
        </ProductSection>
      </div>
    </Page>
  );
}

/**
 * Every tier's cap rows, one card apiece. Open, like everything else inside
 * a product block: the tier filter above is how the list gets shorter.
 *
 * They read the same `/mcp/admin/quota` as the summary above and write
 * nothing, so a separate index would be the same list one click away.
 *
 * `/policy/caps/{tier}` stays its own page: it writes, it carries the
 * resolver warning at the moment a `search_*` tool is
 * capped, and it holds the quota audit. A list and an editor are different
 * kinds of surface.
 */
function CapRows({
  config,
  quota,
  tierFilter,
  tiers,
}: {
  config: AppConfig;
  quota: ReturnType<typeof useResource<McpQuotaAll>>;
  /** The tier the metric row narrowed to, or null for every tier. */
  tierFilter: string | null;
  /** From GET /admin/tiers, not the quota document keys and not known_tiers. */
  tiers: string[];
}) {
  const catalog = useResource<McpCatalog>(config, "/mcp/admin/catalog");
  const s = quota.state;

  if (s.kind !== "ready") {
    return <ResourceView state={s} what="tier caps" onRetry={quota.reload} />;
  }

  const docs = tierDocsOf(s.data);
  const listed = [...tiers].sort().filter((t) => !tierFilter || t === tierFilter);

  return (
    <>
      <Notice title="Reading a cap">
        <span className="ref">-1</span> unlimited · <span className="ref">0</span> gated, never
        allowed · <span className="ref">N</span> metered, N credits a day resetting at 00:00
        UTC. A tool with no cap of its own inherits the tier's{" "}
        <span className="ref">tool_default</span>, unlimited when unset.
      </Notice>

      {/* Catalog note: the old page also rendered a "Tools & resolvers"
          reference list here. It answered "what exists", not "what does this
          tier cost", and made the page long without belonging to it. The
          catalog is still fetched by the editor, where capping happens. */}
      {listed.length === 0 && tiers.length === 0 && (
        <Notice title="No tiers returned">The response carried an empty tier list.</Notice>
      )}
      {listed.length === 0 && tiers.length > 0 && (
        <Notice title="Nothing matches">The filter matched nothing.</Notice>
      )}
      {listed.map((tier) => {
        const entries = capEntries(docs[tier] ?? {});

        return (
          <Card
            key={tier}
            title={<span className="ref">{tier}</span>}
            note={
              <>
                {entries.length} cap{entries.length === 1 ? "" : "s"} stored
              </>
            }
            aside={
              <Button outline href={`/policy/caps/${encodeURIComponent(tier)}`}>
                Open
              </Button>
            }
          >
            {entries.length === 0 ? (
              <EmptyByDesign scope={`tier ${tier}`} />
            ) : (
              <Table
                className="[--gutter:0px]"
                head={
                  <tr>
                    <Th>Tool</Th>
                    <Th right>Cap</Th>
                  </tr>
                }
              >
                {entries.map(({ key, cap, note }) => {
                  const c = capLabel(cap);
                  return (
                    <tr key={key}>
                      <Td>
                        <span className="ref">{key}</span>
                        {note && (
                          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                            {note}
                          </span>
                        )}
                      </Td>
                      <Td right>
                        <Badge tone={c.tone}>{c.text}</Badge>
                      </Td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Card>
        );
      })}
      {catalog.state.kind === "denied" && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          The tool catalog is not readable with your access, so a cap row cannot be
          checked against the tools that exist.
        </p>
      )}
    </>
  );
}

/**
 * A tier document's stored caps as rows: the two tier-wide limits first (they
 * have the widest effect), then each tool's own cap. Only what the tier
 * stores — an absent key is not a row.
 */
function capEntries(doc: McpQuotaDoc): { key: string; cap: number; note?: string }[] {
  const rows: { key: string; cap: number; note?: string }[] = [];
  if (typeof doc.max_calls_per_day === "number") {
    rows.push({ key: "max_calls_per_day", cap: doc.max_calls_per_day, note: "daily total across every tool" });
  }
  if (typeof doc.tool_default === "number") {
    rows.push({ key: "tool_default", cap: doc.tool_default, note: "for every tool without its own cap" });
  }
  for (const [tool, cap] of Object.entries(toolCapsOf(doc)).sort(([a], [b]) => a.localeCompare(b))) {
    rows.push({ key: tool, cap });
  }
  return rows;
}

/**
 * The fleet figures for this page, and the tier filter they drive.
 *
 * Each stat is the way INTO what it counts: clicking a tier narrows both
 * product blocks to it, so "8 merchants on Plus" leads to those eight rather
 * than leaving an operator to find them. Clicking the active one clears.
 *
 * `tier_distribution` is per-tier, so it renders one stat per tier — which is
 * the same data the "Tenants on each tier" chart below shows, made actionable
 * instead of decorative.
 */
function TierMetrics({
  tenantsCount,
  usageToday,
  overrides,
  tierFilter,
  onFilter,
}: {
  tenantsCount: ReturnType<typeof useResource<FleetMetricEnvelope<"tenants_count">>>;
  usageToday: ReturnType<typeof useResource<FleetMetricEnvelope<"usage_today">>>;
  overrides: ReturnType<typeof useResource<FleetMetricEnvelope<"quota_overrides_count">>>;
  tierFilter: string | null;
  onFilter: (tier: string | null) => void;
}) {
  // Through `statOf`: "…" only while a metric loads, a stated failure once it
  // has failed, and never 0 — a zero an operator reads as a fact is worse
  // than an obvious gap, and these are the numbers decisions hang on.
  const overrideTotal = (env: FleetMetricEnvelope<"quota_overrides_count">) => {
    const counts = env.value;
    return counts
      ? Object.values(counts).reduce<number>((n, v) => n + (typeof v === "number" ? v : 0), 0)
      : null;
  };
  const overrideFigure = statOf(overrides.state, overrideTotal);

  return (
    <StatRow>
      <Stat label="Merchants" {...statOf(tenantsCount.state, (env) => env.value)} />
      <Stat label="Credits today" {...statOf(usageToday.state, (env) => env.value?.total_credits)} />
      <Stat
        label="Cap overrides"
        value={overrideFigure.value}
        hint={overrideFigure.hint ?? "tools with their own row, across every tier"}
      />
      {tierFilter && (
        // The escape hatch. The per-tier stats live in the chart below, so
        // without this a filter set there could only be cleared by going back
        // to find the same control.
        <Stat
          value="Clear"
          label={`Filtered to ${tierFilter}`}
          onClick={() => onFilter(null)}
          active
        />
      )}
    </StatRow>
  );
}
